import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import {
  buildTemuPath,
  normalizeTemuShopTypeSlug,
} from '@/temu-platform/constants/temuRoutes';
import {
  buildMarketingEnrollProductList,
  marketingNeedsSessionIds,
  parseMarketingProductSkcItems,
  resolveMarketingProductCategory,
  resolveMarketingProductCategoryId,
  summarizeMarketingEnrollResult,
} from '@/temu-platform/utils/temuMarketingEnroll';
import CategoryCascaderSelect from '@/temu-platform/components/CategoryCascaderSelect';
import TemuOpsPager, {
  TEMU_OPS_DEFAULT_PAGE_SIZE,
  TEMU_OPS_PAGE_SIZE_OPTIONS,
} from '@/temu-platform/components/TemuOpsPager';
import MarketingSitesCell from '@/temu-platform/modules/ops/components/MarketingSitesCell';
import MarketingEnrollWizardModal from '@/temu-platform/modules/ops/MarketingEnrollWizardModal';
import MarketingEnrollBatchFillModal, {
  calcDeclarePriceByDiscount,
} from '@/temu-platform/modules/ops/MarketingEnrollBatchFillModal';
import BatchQueryModal from '@/temu-platform/modules/products/components/BatchQueryModal';
import {
  joinBatchTokens,
  parseBatchTokens,
} from '@/temu-platform/modules/products/components/batchQueryUtils';
import HoverCopyField from '@/temu-platform/modules/products/components/HoverCopyField';
import TemuProductThumb from '@/temu-platform/modules/products/components/TemuProductThumb';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-drawer.css';
import '@/styles/temu-platform/temu-product-ops.css';

function defaultStock(product) {
  const n = Number(product?.suggestActivityStock ?? product?.targetActivityStock);
  return Number.isFinite(n) && n > 0 ? String(Math.trunc(n)) : '';
}

function formatMoney(value, currency) {
  if (value == null || value === '') return '—';
  const raw = String(value).trim();
  if (!raw) return '—';
  if (raw.startsWith('$') || raw.startsWith('¥') || raw.startsWith('€')) return raw;
  const prefix = currency === 'CNY' || currency === 'RMB'
    ? '¥'
    : (currency === 'USD' ? '$' : (currency ? `${currency} ` : '$'));
  return `${prefix}${raw}`;
}

function defaultDeclarePrice(skc) {
  const n = Number(skc?.activityPrice);
  return Number.isFinite(n) && n > 0 ? String(Math.trunc(n)) : '';
}

const ENROLL_COL_SPAN = 10;

const EMPTY_FILTER = {
  categoryId: '',
  categoryLabel: '',
  spu: '',
  skc: '',
  supplierCode: '',
};

const BATCH_FIELD_META = {
  spu: { title: '批量查询 SPU', placeholder: '每行一个 SPU，或用逗号分隔' },
  skc: { title: '批量查询 SKC', placeholder: '每行一个 SKC，或用逗号分隔' },
  supplierCode: { title: '批量查询货号', placeholder: '每行一个货号，或用逗号分隔' },
};

function matchesAnyToken(haystack, tokens) {
  if (!Array.isArray(tokens) || tokens.length === 0) return true;
  const h = String(haystack ?? '').trim().toLowerCase();
  if (!h) return false;
  return tokens.some((token) => {
    const t = String(token || '').trim().toLowerCase();
    return t && (h === t || h.includes(t));
  });
}

function productMatchesCategory(row, categoryId, categoryLabel) {
  if (!categoryId) return true;
  const id = resolveMarketingProductCategoryId(row);
  if (id && String(id) === String(categoryId)) return true;
  const label = resolveMarketingProductCategory(row);
  if (categoryLabel && label) {
    const a = String(categoryLabel).toLowerCase();
    const b = String(label).toLowerCase();
    if (a.includes(b) || b.includes(a)) return true;
  }
  return false;
}

/**
 * 活动报名独立页：单表选品 + 提交
 */
export default function TemuMarketingEnrollPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const { shopType, thematicId: thematicIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const track = normalizeTemuShopTypeSlug(shopType) || 'semi';

  const shopId = String(searchParams.get('shopId') || '').trim();
  const activityType = Number(searchParams.get('activityType'));
  const activityName = String(searchParams.get('name') || '').trim();
  const typeLabel = String(searchParams.get('typeLabel') || '').trim();
  const thematicId = thematicIdParam;

  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [products, setProducts] = useState([]);
  const [scrollContext, setScrollContext] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const [selectedProductIds, setSelectedProductIds] = useState(() => new Set());
  const [stockDrafts, setStockDrafts] = useState({});
  const [priceDrafts, setPriceDrafts] = useState({});

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardProducts, setWizardProducts] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState('');
  const [sessions, setSessions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [filterDraft, setFilterDraft] = useState(() => ({ ...EMPTY_FILTER }));
  const [appliedFilter, setAppliedFilter] = useState(() => ({ ...EMPTY_FILTER }));
  const [batchModalField, setBatchModalField] = useState(null);
  const [categories, setCategories] = useState([]);
  const [batchFillKind, setBatchFillKind] = useState(null); // 'price' | 'stock' | null
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);

  const needsSessions = marketingNeedsSessionIds(track, activityType);
  const paramsReady = Boolean(shopId && thematicId && Number.isFinite(activityType));

  useEffect(() => {
    document.title = t('marketing.enPageTitleTemu');
  }, [t]);

  useEffect(() => {
    if (!shopId) {
      setCategories([]);
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getShopCategories(shopId)
      .then((res) => {
        if (cancelled) return;
        setCategories(res?.categories ?? res?.items ?? res?.list ?? res ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => { cancelled = true; };
  }, [shopId]);

  const loadDetail = useCallback(async () => {
    if (!paramsReady) return;
    setDetailLoading(true);
    try {
      const res = await temuPlatformAPI.getMarketingActivityDetail(shopId, thematicId, { activityType });
      setDetail(res?.result || null);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [paramsReady, shopId, thematicId, activityType]);

  const seedDraftsFromProducts = useCallback((items, { reset = false } = {}) => {
    setStockDrafts((prev) => {
      const next = reset ? {} : { ...prev };
      items.forEach((row) => {
        const key = String(row.productId);
        if (reset || next[key] == null || next[key] === '') {
          next[key] = defaultStock(row);
        }
      });
      return next;
    });
    setPriceDrafts((prev) => {
      const next = reset ? {} : { ...prev };
      items.forEach((row) => {
        const pKey = String(row.productId);
        if (!next[pKey]) next[pKey] = {};
        parseMarketingProductSkcItems(row).forEach((skc) => {
          if (skc.skcId == null) return;
          const sKey = String(skc.skcId);
          if (reset || next[pKey][sKey] == null || next[pKey][sKey] === '') {
            next[pKey][sKey] = defaultDeclarePrice(skc);
          }
        });
      });
      return next;
    });
  }, []);

  const loadProducts = useCallback(async ({ reset = false, context } = {}) => {
    if (!paramsReady) return;
    setProductsLoading(true);
    setProductsError('');
    try {
      const nextContext = reset ? undefined : (context !== undefined ? context : (scrollContext || undefined));
      const res = await temuPlatformAPI.getMarketingActivityProducts(shopId, thematicId, {
        activityType,
        rowCount: Math.max(50, listPageSize),
        searchScrollContext: nextContext,
      });
      const items = Array.isArray(res?.items) ? res.items : [];
      setProducts((prev) => (reset ? items : [...prev, ...items]));
      setScrollContext(res?.searchScrollContext || null);
      setHasMore(Boolean(res?.hasMore));
      if (res?.success === false && res?.errorMsg) {
        setProductsError(res.errorMsg);
      }
      seedDraftsFromProducts(items, { reset });
    } catch (e) {
      if (reset) setProducts([]);
      setProductsError(e?.message || '加载活动商品失败');
    } finally {
      setProductsLoading(false);
    }
  }, [paramsReady, shopId, thematicId, activityType, scrollContext, seedDraftsFromProducts, listPageSize]);

  const loadSessions = useCallback(async (productIds) => {
    const ids = (Array.isArray(productIds) ? productIds : [productIds])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!paramsReady || ids.length === 0) {
      setSessions([]);
      return;
    }
    setSessionsLoading(true);
    setSessionsError('');
    try {
      const res = await temuPlatformAPI.getMarketingActivitySessions(shopId, thematicId, {
        activityType,
        productIds: ids,
      });
      const rawItems = Array.isArray(res?.items) ? res.items : [];
      const seen = new Set();
      const items = [];
      rawItems.forEach((row) => {
        const sid = row?.sessionId;
        if (sid != null) {
          const key = String(sid);
          if (seen.has(key)) return;
          seen.add(key);
        }
        items.push(row);
      });
      setSessions(items);
      if (res?.success === false && res?.errorMsg) {
        setSessionsError(res.errorMsg);
      }
    } catch (e) {
      setSessions([]);
      setSessionsError(e?.message || '加载场次失败');
    } finally {
      setSessionsLoading(false);
    }
  }, [paramsReady, shopId, thematicId, activityType]);

  useEffect(() => {
    if (!paramsReady) return;
    setDetail(null);
    setProducts([]);
    setScrollContext(null);
    setHasMore(false);
    setSelectedProductIds(new Set());
    setStockDrafts({});
    setPriceDrafts({});
    setWizardOpen(false);
    setWizardProducts([]);
    setSessions([]);
    setProductsError('');
    setSessionsError('');
    setSubmitting(false);
    setFilterDraft({ ...EMPTY_FILTER });
    setAppliedFilter({ ...EMPTY_FILTER });
    setListPage(1);
    loadDetail();
    loadProducts({ reset: true, context: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsReady, shopId, thematicId, activityType]);

  const productById = useMemo(() => {
    const map = new Map();
    products.forEach((row) => {
      if (row?.productId != null) map.set(String(row.productId), row);
    });
    return map;
  }, [products]);

  const filteredProducts = useMemo(() => {
    const spuTokens = parseBatchTokens(appliedFilter.spu);
    const skcTokens = parseBatchTokens(appliedFilter.skc);
    const codeTokens = parseBatchTokens(appliedFilter.supplierCode);
    const categoryId = String(appliedFilter.categoryId || '').trim();
    const categoryLabel = String(appliedFilter.categoryLabel || '').trim();
    if (!spuTokens.length && !skcTokens.length && !codeTokens.length && !categoryId) {
      return products;
    }

    return products.filter((row) => {
      if (!productMatchesCategory(row, categoryId, categoryLabel)) return false;
      if (spuTokens.length && !matchesAnyToken(row?.productId, spuTokens)) return false;
      if (!skcTokens.length && !codeTokens.length) return true;
      const skcItems = parseMarketingProductSkcItems(row);
      return skcItems.some((item) => {
        if (skcTokens.length
          && !matchesAnyToken(item.skcName, skcTokens)
          && !matchesAnyToken(item.skcId, skcTokens)) {
          return false;
        }
        if (codeTokens.length && !matchesAnyToken(item.supplierCode, codeTokens)) {
          return false;
        }
        return true;
      });
    });
  }, [products, appliedFilter]);

  const visibleRows = useMemo(() => {
    const skcTokens = parseBatchTokens(appliedFilter.skc);
    const codeTokens = parseBatchTokens(appliedFilter.supplierCode);
    return filteredProducts.map((row) => {
      const allSkc = parseMarketingProductSkcItems(row);
      if (!skcTokens.length && !codeTokens.length) {
        return { row, skcItems: allSkc };
      }
      const skcItems = allSkc.filter((item) => {
        if (skcTokens.length
          && !matchesAnyToken(item.skcName, skcTokens)
          && !matchesAnyToken(item.skcId, skcTokens)) {
          return false;
        }
        if (codeTokens.length && !matchesAnyToken(item.supplierCode, codeTokens)) {
          return false;
        }
        return true;
      });
      return { row, skcItems: skcItems.length ? skcItems : allSkc };
    });
  }, [filteredProducts, appliedFilter]);

  const totalListPages = Math.max(1, Math.ceil(visibleRows.length / Math.max(1, listPageSize)) || 1);
  const safeListPage = Math.min(Math.max(1, listPage), totalListPages);
  const pagedRows = useMemo(() => {
    const start = (safeListPage - 1) * listPageSize;
    return visibleRows.slice(start, start + listPageSize);
  }, [visibleRows, safeListPage, listPageSize]);

  useEffect(() => {
    setListPage(1);
  }, [appliedFilter]);

  useEffect(() => {
    if (listPage !== safeListPage) setListPage(safeListPage);
  }, [listPage, safeListPage]);

  const handleListPageChange = (nextPage) => {
    const next = Math.max(1, Number(nextPage) || 1);
    if (next > totalListPages && hasMore && !productsLoading) {
      loadProducts({ reset: false }).finally(() => {
        setListPage(next);
      });
      return;
    }
    setListPage(next);
  };

  const pageSelectableKeys = useMemo(
    () => pagedRows
      .map(({ row }) => (row?.productId != null ? String(row.productId) : ''))
      .filter(Boolean),
    [pagedRows],
  );

  const allSelected = pageSelectableKeys.length > 0
    && pageSelectableKeys.every((key) => selectedProductIds.has(key));

  const onQuery = () => {
    setAppliedFilter({
      categoryId: String(filterDraft.categoryId || '').trim(),
      categoryLabel: String(filterDraft.categoryLabel || '').trim(),
      spu: String(filterDraft.spu || '').trim(),
      skc: String(filterDraft.skc || '').trim(),
      supplierCode: String(filterDraft.supplierCode || '').trim(),
    });
    setListPage(1);
  };

  const onResetFilter = () => {
    setFilterDraft({ ...EMPTY_FILTER });
    setAppliedFilter({ ...EMPTY_FILTER });
    setListPage(1);
  };

  const openBatchModal = (field) => setBatchModalField(field);

  const onBatchConfirm = (tokens) => {
    if (!batchModalField) return;
    setFilterDraft((prev) => ({
      ...prev,
      [batchModalField]: joinBatchTokens(tokens),
    }));
  };

  const selectedCount = selectedProductIds.size;

  const toggleProduct = (productId) => {
    const key = String(productId);
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllProducts = () => {
    setSelectedProductIds((prev) => {
      if (allSelected) {
        const next = new Set(prev);
        pageSelectableKeys.forEach((key) => next.delete(key));
        return next;
      }
      const next = new Set(prev);
      pageSelectableKeys.forEach((key) => next.add(key));
      return next;
    });
  };

  const closeEnrollWizard = (force = false) => {
    if (submitting && !force) return;
    setWizardOpen(false);
    setWizardProducts([]);
    setSessions([]);
    setSessionsError('');
  };

  const openEnrollWizard = (productsToEnroll) => {
    const list = (Array.isArray(productsToEnroll) ? productsToEnroll : [])
      .filter((row) => row?.productId != null);
    if (!list.length) {
      showToast(fixedUi("请先选择要报名的商品"), 'warning');
      return;
    }
    setWizardProducts(list);
    setWizardOpen(true);
    setSessions([]);
    setSessionsError('');
    if (needsSessions) {
      loadSessions(list.map((row) => row.productId));
    }
  };

  const openSingleEnroll = (product) => {
    if (!product) return;
    openEnrollWizard([product]);
  };

  const openBatchEnroll = () => {
    const selected = Array.from(selectedProductIds)
      .map((id) => productById.get(id))
      .filter(Boolean);
    openEnrollWizard(selected);
  };

  const resolveBatchTargetProducts = () => {
    if (selectedProductIds.size > 0) {
      return Array.from(selectedProductIds)
        .map((id) => productById.get(id))
        .filter(Boolean);
    }
    return filteredProducts;
  };

  const handleBatchFillConfirm = (payload) => {
    const targets = resolveBatchTargetProducts();
    if (!targets.length) {
      showToast(fixedUi("当前没有可填写的商品"), 'warning');
      return;
    }

    if (payload?.kind === 'stock') {
      const stock = Number(payload.value);
      if (!Number.isFinite(stock) || stock <= 0) {
        showToast(fixedUi("请输入有效的活动库存"), 'warning');
        return;
      }
      setStockDrafts((prev) => {
        const next = { ...prev };
        targets.forEach((row) => {
          next[String(row.productId)] = String(Math.trunc(stock));
        });
        return next;
      });
      showToast(`已批量填写活动库存（${targets.length} 个商品）`, 'success');
      return;
    }

    if (payload?.kind === 'price') {
      if (payload.mode === 'fixed') {
        const price = Number(payload.value);
        if (!Number.isFinite(price) || price <= 0) {
          showToast(fixedUi("请输入有效的申报价"), 'warning');
          return;
        }
        const fixed = String(Math.trunc(price));
        setPriceDrafts((prev) => {
          const next = { ...prev };
          targets.forEach((row) => {
            const pKey = String(row.productId);
            const skcMap = { ...(next[pKey] || {}) };
            parseMarketingProductSkcItems(row).forEach((skc) => {
              if (skc.skcId == null) return;
              skcMap[String(skc.skcId)] = fixed;
            });
            next[pKey] = skcMap;
          });
          return next;
        });
        showToast(`已按统一报价批量填写（${targets.length} 个商品）`, 'success');
        return;
      }

      // discount
      let filled = 0;
      const nextByProduct = {};
      targets.forEach((row) => {
        const pKey = String(row.productId);
        const skcMap = {};
        parseMarketingProductSkcItems(row).forEach((skc) => {
          if (skc.skcId == null) return;
          const priced = calcDeclarePriceByDiscount(skc.supplyPrice, payload.value);
          if (priced == null) return;
          skcMap[String(skc.skcId)] = priced;
          filled += 1;
        });
        nextByProduct[pKey] = skcMap;
      });
      if (!filled) {
        showToast(fixedUi("未能根据供货价计算申报价，请检查供货价是否完整"), 'warning');
        return;
      }
      setPriceDrafts((prev) => {
        const next = { ...prev };
        Object.entries(nextByProduct).forEach(([pKey, skcMap]) => {
          next[pKey] = { ...(next[pKey] || {}), ...skcMap };
        });
        return next;
      });
      showToast(`已按供货价折扣批量填写（${filled} 个 SKC）`, 'success');
    }
  };

  const goBackToList = () => {
    navigate(buildTemuPath(track, 'marketing'));
  };

  const submitEnrollWithWizard = async ({ products: enrollProducts, sessionIds, siteIds }) => {
    if (!paramsReady) return;
    const list = Array.isArray(enrollProducts) ? enrollProducts : [];
    if (!list.length) {
      showToast(fixedUi("请先选择要报名的商品"), 'warning');
      return;
    }
    if (needsSessions && (!Array.isArray(sessionIds) || sessionIds.length === 0)) {
      showToast(fixedUi("请先选择场次"), 'warning');
      return;
    }
    if (!Array.isArray(siteIds) || siteIds.length === 0) {
      showToast(fixedUi("请先选择站点"), 'warning');
      return;
    }

    let productList;
    try {
      const drafts = {};
      list.forEach((row) => {
        const key = String(row.productId);
        drafts[key] = {
          activityStock: stockDrafts[key],
          sessionIds: needsSessions ? sessionIds : [],
          siteIds,
          skcActivityPrices: priceDrafts[key] || {},
        };
      });
      productList = buildMarketingEnrollProductList(list, drafts);
    } catch (e) {
      showToast(e?.message || '报名参数不完整', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const started = await temuPlatformAPI.submitMarketingEnroll(shopId, thematicId, {
        activityType,
        productList,
      });
      let res = started;
      const jobId = started?.jobId || started?.id;
      if (jobId && !started?.done) {
        while (res && !res.done) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 700));
          // eslint-disable-next-line no-await-in-loop
          res = await temuPlatformAPI.getMarketingEnrollJob(shopId, jobId);
        }
      }
      const payload = res?.result && typeof res.result === 'object' ? res.result : res;
      const msg = summarizeMarketingEnrollResult(payload);
      const ok = Boolean(res?.ok)
        || (Number(payload?.successCount) || 0) > 0
        || (payload?.success !== false && (Number(payload?.failCount) || 0) === 0);
      const refreshed = payload?.enrollCacheRefreshed;
      const hint = ok && (Number(payload?.successCount) || 0) > 0
        ? `${msg}${refreshed === false ? '（本地报名缓存刷新失败，请手动同步报名记录）' : '。可到「报名记录」查看最新状态'}`
        : (res?.message || msg);
      showToast(hint, ok ? 'success' : 'warning');
      if (ok) closeEnrollWizard(true);
    } catch (e) {
      showToast(e?.message || t('marketing.enEnrollFail'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const thematicInfo = detail?.thematicInfo || detail?.activityInfo || {};
  const canEnroll = detail?.activityInfo?.canEnroll ?? detail?.canEnroll;
  const displayName = thematicInfo.activityThematicName
    || detail?.activityInfo?.activityName
    || activityName
    || t('marketing.enPageTitle');
  const enrollSourceLabel = thematicInfo.enrollSource === 1
    ? fixedUi("自主报名")
    : (thematicInfo.enrollSource === 0 ? '邀约报名' : '');

  if (!paramsReady) {
    return (
      <div className="tpo-enroll">
        <div className="tpo-enroll__empty-state">
          <h1>{t('marketing.enPageTitle')}</h1>
          <p>{t('marketing.enMissingParams')}</p>
          <button type="button" className="tpo-btn tpo-btn--secondary" onClick={goBackToList}>
            {t('marketing.enBackList')}
          </button>
        </div>
      </div>
    );
  }

  const renderProductRows = () => pagedRows.flatMap(({ row, skcItems }) => {
    const key = String(row.productId);
    const selected = selectedProductIds.has(key);
    const rowSpan = Math.max(1, skcItems.length);
    const productTitle = row.productName || row.title || '未命名货品';
    const categoryLabel = resolveMarketingProductCategory(row) || '—';
    const refStock = row.suggestActivityStock ?? row.targetActivityStock;

    return skcItems.map((skc, skcIdx) => {
      const isFirst = skcIdx === 0;
      const currency = skc.currency || row.currency;
      const skcKey = skc.skcId != null ? String(skc.skcId) : `idx-${skcIdx}`;
      const declarePrice = priceDrafts[key]?.[skcKey] ?? defaultDeclarePrice(skc);

      return (
        <tr
          key={`${key}-skc-${skcKey}`}
          className={`temu-product-row temu-draft-row${isFirst ? '' : ' temu-draft-row--skc-child'}${selected ? ' is-selected' : ''}`}
        >
          {isFirst ? (
            <>
              <td
                rowSpan={rowSpan}
                className="tpo-ops-td tpo-ops-td--check temu-product-cell temu-product-cell--spu-check"
                onClick={() => toggleProduct(row.productId)}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  aria-label={`选择货品 ${row.productId}`}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleProduct(row.productId)}
                />
              </td>
              <td rowSpan={rowSpan} className="temu-product-cell temu-product-cell--info">
                <div className="temu-product-info temu-product-info--text-only temu-draft-product-info">
                  <div className="temu-product-info__text">
                    <HoverCopyField
                      value={productTitle}
                      className="temu-product-info__title"
                      popoverCopy
                    />
                    <HoverCopyField
                      label="SPU"
                      value={row.productId != null ? String(row.productId) : ''}
                      className="temu-product-info__meta"
                      separator=": "
                    />
                    <div className="temu-product-info__meta">
                      类目: {categoryLabel}
                    </div>
                  </div>
                </div>
              </td>
            </>
          ) : null}

          <td className="temu-product-cell temu-product-cell--spec">
            <div className="temu-product-spec temu-draft-skc">
              <TemuProductThumb imageUrl={skc.imageUrl || row.productImageUrl} />
              <div className="temu-product-spec__text">
                <div className="temu-product-spec__color">{skc.colorLabel || '—'}</div>
                <HoverCopyField
                  label="SKC"
                  value={skc.skcName || ''}
                  className="temu-product-spec__skc"
                  separator=": "
                />
                <HoverCopyField
                  label={fixedUi("货号")}
                  value={skc.supplierCode || ''}
                  className="temu-product-spec__skc"
                  separator=": "
                  emptyText="—"
                />
              </div>
            </div>
          </td>

          {isFirst ? (
            <td rowSpan={rowSpan} className="temu-product-cell">
              <MarketingSitesCell row={row} />
            </td>
          ) : null}

          <td className="temu-product-cell temu-product-cell--price">
            {formatMoney(skc.supplyPrice, currency)}
          </td>
          <td className="temu-product-cell temu-product-cell--price tpo-enroll-td--ref-price">
            {formatMoney(skc.activityPrice, currency)}
          </td>

          {isFirst ? (
            <td rowSpan={rowSpan} className="temu-product-cell temu-product-cell--stock tpo-enroll-td--ref-stock">
              {refStock ?? '—'}
            </td>
          ) : null}

          <td className="temu-product-cell temu-product-cell--price tpo-enroll-td--declare-price">
            <input
              className="tpo-input tpo-enroll__price-input"
              type="number"
              min={1}
              value={declarePrice}
              aria-label={`货品 ${row.productId} SKC ${skc.skcName || skcKey} 活动申报价`}
              onChange={(e) => {
                const v = e.target.value;
                setPriceDrafts((prev) => ({
                  ...prev,
                  [key]: { ...(prev[key] || {}), [skcKey]: v },
                }));
              }}
            />
          </td>

          {isFirst ? (
            <>
              <td rowSpan={rowSpan} className="temu-product-cell temu-product-cell--stock tpo-enroll-td--activity-stock">
                <input
                  className="tpo-input tpo-enroll__stock-input"
                  type="number"
                  min={1}
                  value={stockDrafts[key] ?? ''}
                  aria-label={`货品 ${row.productId} 活动库存`}
                  onChange={(e) => {
                    const v = e.target.value;
                    setStockDrafts((prev) => ({ ...prev, [key]: v }));
                  }}
                />
              </td>
              <td rowSpan={rowSpan} className="temu-product-cell temu-product-cell--actions tpo-enroll-td--actions">
                <button
                  type="button"
                  className="tpo-btn tpo-btn--sm tpo-btn--primary"
                  disabled={submitting}
                  onClick={() => openSingleEnroll(row)}
                >
                  {t('marketing.enPageTitle')}
                </button>
              </td>
            </>
          ) : null}
        </tr>
      );
    });
  });

  return (
    <div className="tpo-enroll">
      <header className="tpo-enroll__top">
        <div className="tpo-enroll__identity">
          <h1 className="tpo-enroll__title">{displayName}</h1>
          <div className="tpo-enroll__meta">
            {typeLabel ? <span className="tpo-enroll__chip">{typeLabel}</span> : null}
            {thematicId ? <span className="tpo-enroll__chip tpo-enroll__chip--muted">专题 {thematicId}</span> : null}
            {enrollSourceLabel ? <span className="tpo-enroll__chip tpo-enroll__chip--muted">{enrollSourceLabel}</span> : null}
            {canEnroll != null ? (
              <span className={`tpo-enroll__chip ${canEnroll ? 'tpo-enroll__chip--ok' : 'tpo-enroll__chip--warn'}`}>
                {canEnroll ? '可报名' : '暂不可报名'}
              </span>
            ) : (detailLoading ? (
              <span className="tpo-enroll__chip tpo-enroll__chip--muted">{fixedUi("加载中…")}</span>
            ) : null)}
          </div>
        </div>
      </header>

      <div className="tpo-enroll__panel">
        <div className="tpo-enroll__products">
          <div className="tpo-enroll__toolbar tpo-filter-bar">
            <div className="tpo-filter-bar__fields">
              <div className="tpo-field tpo-field--inline tpo-field--category">
                <span className="tpo-field__label">{fixedUi("类目")}</span>
                <CategoryCascaderSelect
                  shopId={shopId}
                  categories={categories}
                  value={filterDraft.categoryId}
                  placeholder={!shopId ? fixedUi("请先选择店铺") : fixedUi("全部类目")}
                  emptyText={!shopId ? fixedUi("请先选择店铺") : '暂无类目'}
                  allowClear
                  onChange={(id, meta) => {
                    setFilterDraft((prev) => ({
                      ...prev,
                      categoryId: id ? String(id) : '',
                      categoryLabel: meta?.label || '',
                    }));
                  }}
                />
              </div>
              <div className="tpo-field tpo-field--inline tpo-field--batch">
                <span className="tpo-field__label">SPU</span>
                <input
                  className="tpo-input"
                  type="search"
                  value={filterDraft.spu}
                  placeholder={fixedUi("SPU，支持逗号批量")}
                  onChange={(e) => setFilterDraft((prev) => ({ ...prev, spu: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                />
                <button
                  type="button"
                  className="tpo-batch-add-btn"
                  aria-label={fixedUi("批量输入 SPU")}
                  title={fixedUi("批量查询")}
                  onClick={() => openBatchModal('spu')}
                >
                  <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
                </button>
              </div>
              <div className="tpo-field tpo-field--inline tpo-field--batch">
                <span className="tpo-field__label">SKC</span>
                <input
                  className="tpo-input"
                  type="search"
                  value={filterDraft.skc}
                  placeholder={fixedUi("SKC，支持逗号批量")}
                  onChange={(e) => setFilterDraft((prev) => ({ ...prev, skc: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                />
                <button
                  type="button"
                  className="tpo-batch-add-btn"
                  aria-label={fixedUi("批量输入 SKC")}
                  title={fixedUi("批量查询")}
                  onClick={() => openBatchModal('skc')}
                >
                  <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
                </button>
              </div>
              <div className="tpo-field tpo-field--inline tpo-field--batch">
                <span className="tpo-field__label">{fixedUi("货号")}</span>
                <input
                  className="tpo-input"
                  type="search"
                  value={filterDraft.supplierCode}
                  placeholder={fixedUi("货号，支持逗号批量")}
                  onChange={(e) => setFilterDraft((prev) => ({ ...prev, supplierCode: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                />
                <button
                  type="button"
                  className="tpo-batch-add-btn"
                  aria-label={fixedUi("批量输入货号")}
                  title={fixedUi("批量查询")}
                  onClick={() => openBatchModal('supplierCode')}
                >
                  <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
                </button>
              </div>
            </div>
            <div className="tpo-filter-bar__actions tpo-enroll__toolbar-side">
              <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onResetFilter}>
                {t('shop.reset')}
              </button>
              <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={onQuery}>
                {t('shop.query')}
              </button>
            </div>
          </div>

          {productsError ? <p className="tpo-enroll__error">{productsError}</p> : null}

          <div className="tpo-enroll__table-panel">
            <div className="tpo-enroll__table-wrap" role="region" aria-label={fixedUi("可报名商品列表")}>
              <table className="wf-tasks-table platform-product-table platform-product-table--temu platform-product-table--drafts tpo-ops-table--sticky-head tpo-enroll__table tpo-enroll__table--flat">
              <colgroup>
                <col className="tpo-enroll-col--check" />
                <col className="tpo-enroll-col--info" />
                <col className="tpo-enroll-col--skc" />
                <col className="tpo-enroll-col--sites" />
                <col className="tpo-enroll-col--price" />
                <col className="tpo-enroll-col--ref-price" />
                <col className="tpo-enroll-col--ref-stock" />
                <col className="tpo-enroll-col--declare-price" />
                <col className="tpo-enroll-col--activity-stock" />
                <col className="tpo-enroll-col--actions" />
              </colgroup>
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="tpo-ops-th--check"
                    onClick={() => {
                      if (pageSelectableKeys.length === 0) return;
                      toggleAllProducts();
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      disabled={pageSelectableKeys.length === 0}
                      aria-label={fixedUi("全选当前页商品")}
                      onClick={(e) => e.stopPropagation()}
                      onChange={toggleAllProducts}
                    />
                  </th>
                  <th scope="col">{fixedUi("产品信息")}</th>
                  <th scope="col">{fixedUi("skc信息")}</th>
                  <th scope="col">{fixedUi("经营站点")}</th>
                  <th scope="col">{fixedUi("供货价")}</th>
                  <th scope="col" className="tpo-enroll-th--ref-price">{fixedUi("参考活动价")}</th>
                  <th scope="col" className="tpo-enroll-th--ref-stock">{fixedUi("参考活动库存")}</th>
                  <th scope="col" className="tpo-enroll-th--declare-price">
                    <span className="tpo-supply-th tpo-enroll-th__with-batch">
                      <span>{fixedUi("活动申报价")}</span>
                      <button
                        type="button"
                        className="tpo-supply-th__batch"
                        onClick={() => setBatchFillKind('price')}
                      >
                        {t('marketing.enBatch')}
                      </button>
                    </span>
                  </th>
                  <th scope="col" className="tpo-enroll-th--activity-stock">
                    <span className="tpo-supply-th tpo-enroll-th__with-batch">
                      <span>{fixedUi("活动库存")}</span>
                      <button
                        type="button"
                        className="tpo-supply-th__batch"
                        onClick={() => setBatchFillKind('stock')}
                      >
                        {t('marketing.enBatch')}
                      </button>
                    </span>
                  </th>
                  <th scope="col" className="tpo-enroll-th--actions">{fixedUi("操作")}</th>
                </tr>
              </thead>
              <tbody>
                {productsLoading && products.length === 0 ? (
                  <tr>
                    <td colSpan={ENROLL_COL_SPAN} className="tpo-enroll__empty-cell">
                      <div className="tpo-enroll__empty">
                        <p className="tpo-enroll__empty-title">{fixedUi("正在加载可报商品…")}</p>
                      </div>
                    </td>
                  </tr>
                ) : null}
                {!productsLoading && products.length === 0 ? (
                  <tr>
                    <td colSpan={ENROLL_COL_SPAN} className="tpo-enroll__empty-cell">
                      <div className="tpo-enroll__empty">
                        <p className="tpo-enroll__empty-title">{fixedUi("暂无可报商品")}</p>
                        <p className="tpo-enroll__empty-hint">{fixedUi("请确认活动是否开放报名，或稍后再试。")}</p>
                      </div>
                    </td>
                  </tr>
                ) : null}
                {!productsLoading && products.length > 0 && visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={ENROLL_COL_SPAN} className="tpo-enroll__empty-cell">
                      <div className="tpo-enroll__empty">
                        <p className="tpo-enroll__empty-title">{fixedUi("无匹配商品")}</p>
                        <p className="tpo-enroll__empty-hint">{fixedUi("请调整查询条件后重试。")}</p>
                      </div>
                    </td>
                  </tr>
                ) : null}
                {pagedRows.length > 0 ? renderProductRows() : null}
              </tbody>
            </table>
            </div>
            <div className="tpo-enroll__pager">
              <TemuOpsPager
                page={safeListPage}
                pageSize={listPageSize}
                total={hasMore ? undefined : visibleRows.length}
                pageItemCount={visibleRows.length}
                hasNext={safeListPage < totalListPages || hasMore}
                loading={productsLoading}
                onPageChange={handleListPageChange}
                onPageSizeChange={(size) => {
                  setListPageSize(size);
                  setListPage(1);
                }}
                pageSizeOptions={TEMU_OPS_PAGE_SIZE_OPTIONS}
                ariaLabel="可报名商品分页"
              />
            </div>
          </div>
          {productsLoading && products.length > 0 ? (
            <p className="tpo-enroll__muted tpo-enroll__loading-more">{fixedUi("加载更多中…")}</p>
          ) : null}
        </div>
      </div>

      <footer className="tpo-enroll__foot">
        <div className="tpo-enroll__foot-summary">
          <span>{fixedUi("已选商品")} <strong>{selectedCount}</strong></span>
          {needsSessions ? (
            <span className="tpo-enroll__foot-sep">{fixedUi("勾选后可批量报名（先选场次，再选站点）")}</span>
          ) : (
            <span className="tpo-enroll__foot-sep">{fixedUi("勾选后可批量报名（选择站点后提交）")}</span>
          )}
        </div>
        <div className="tpo-enroll__foot-actions">
          <button type="button" className="tpo-btn tpo-btn--secondary" onClick={goBackToList} disabled={submitting}>
            {t('shop.cancel')}
          </button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary"
            disabled={submitting || selectedCount === 0}
            onClick={openBatchEnroll}
          >
            {submitting ? fixedUi("提交中…") : `批量报名（${selectedCount}）`}
          </button>
        </div>
      </footer>

      <MarketingEnrollWizardModal
        open={wizardOpen}
        products={wizardProducts}
        needsSessions={needsSessions}
        sessions={sessions}
        sessionsLoading={sessionsLoading}
        sessionsError={sessionsError}
        submitting={submitting}
        onClose={() => closeEnrollWizard()}
        onSubmit={submitEnrollWithWizard}
      />

      <MarketingEnrollBatchFillModal
        open={Boolean(batchFillKind)}
        kind={batchFillKind || 'price'}
        onClose={() => setBatchFillKind(null)}
        onConfirm={handleBatchFillConfirm}
      />

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={batchModalField ? (BATCH_FIELD_META[batchModalField]?.title || fixedUi("批量查询")) : fixedUi("批量查询")}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={batchModalField ? BATCH_FIELD_META[batchModalField]?.placeholder : undefined}
        onConfirm={onBatchConfirm}
        onClose={() => setBatchModalField(null)}
      />
    </div>
  );
}
